export type RevenueWindowSummary = {
  billedAmount: number;
  chargedAmount: number;
  claimCoveragePct: number;
  claimedEntries: number;
  claims: number;
  collectionRatePct: number;
  entries: number;
  outstandingAmount: number;
  paidAmount: number;
};

export type RevenueDeltas = {
  billedAmount: number | null;
  claimCoveragePct: number | null;
  collectionRatePct: number | null;
  entries: number | null;
  outstandingAmount: number | null;
  paidAmount: number | null;
};

export type RevenueTrendPoint = {
  billedAmount: number;
  claimCoveragePct: number;
  claimedEntries: number;
  claims: number;
  collectionRatePct: number;
  entries: number;
  monthStart: string;
  outstandingAmount: number;
  paidAmount: number;
};

export type RevenueClinicRow = {
  billedAmount: number;
  claimCoveragePct: number;
  claimedEntries: number;
  claims: number;
  clinic: string;
  collectionRatePct: number;
  entries: number;
  outstandingAmount: number;
  paidAmount: number;
};

export type RevenueHcpcsMixRow = {
  currentMixPct: number;
  hcpcs: string;
  prior1MixPct: number;
  prior2MixPct: number;
};

export type RevenueQueueSummaryRow = {
  monthStart: string;
  noClaimYetCount: number;
  otherCount: number;
  paidCount: number;
  processingCount: number;
  rejectedCount: number;
  respiteBalanceCount: number;
  writtenOffCount: number;
};

export type RevenueSnapshotResponse = {
  asOf: string;
  current: RevenueWindowSummary;
  deltas: RevenueDeltas;
  generatedAt: string;
  hcpcsMix: RevenueHcpcsMixRow[];
  monthStart: string;
  prior: RevenueWindowSummary;
  priorMonthEnd: string;
  priorMonthStart: string;
  queueSummary: RevenueQueueSummaryRow[];
  source: string;
  topClinics: RevenueClinicRow[];
  trend: RevenueTrendPoint[];
};

function isFiniteNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isNullableNumber(value: unknown): value is number | null {
  return value === null || isFiniteNumber(value);
}

function isWindowSummary(value: unknown): value is RevenueWindowSummary {
  if (!value || typeof value !== "object") return false;
  const row = value as RevenueWindowSummary;
  return (
    isFiniteNumber(row.entries) &&
    isFiniteNumber(row.claimedEntries) &&
    isFiniteNumber(row.claims) &&
    isFiniteNumber(row.billedAmount) &&
    isFiniteNumber(row.paidAmount) &&
    isFiniteNumber(row.chargedAmount) &&
    isFiniteNumber(row.outstandingAmount) &&
    isFiniteNumber(row.claimCoveragePct) &&
    isFiniteNumber(row.collectionRatePct)
  );
}

function isTrendPoint(value: unknown): value is RevenueTrendPoint {
  if (!value || typeof value !== "object") return false;
  const row = value as RevenueTrendPoint;
  return typeof row.monthStart === "string" && isFiniteNumber(row.billedAmount);
}

function isClinicRow(value: unknown): value is RevenueClinicRow {
  if (!value || typeof value !== "object") return false;
  const row = value as RevenueClinicRow;
  return typeof row.clinic === "string" && isFiniteNumber(row.billedAmount);
}

function isHcpcsRow(value: unknown): value is RevenueHcpcsMixRow {
  if (!value || typeof value !== "object") return false;
  const row = value as RevenueHcpcsMixRow;
  return (
    typeof row.hcpcs === "string" &&
    isFiniteNumber(row.currentMixPct) &&
    isFiniteNumber(row.prior1MixPct) &&
    isFiniteNumber(row.prior2MixPct)
  );
}

function isQueueRow(value: unknown): value is RevenueQueueSummaryRow {
  if (!value || typeof value !== "object") return false;
  const row = value as RevenueQueueSummaryRow;
  return typeof row.monthStart === "string" && isFiniteNumber(row.paidCount);
}

export function isRevenueSnapshotResponse(value: unknown): value is RevenueSnapshotResponse {
  if (!value || typeof value !== "object") return false;
  const data = value as Partial<RevenueSnapshotResponse>;
  return (
    typeof data.asOf === "string" &&
    typeof data.monthStart === "string" &&
    typeof data.priorMonthStart === "string" &&
    typeof data.priorMonthEnd === "string" &&
    typeof data.source === "string" &&
    typeof data.generatedAt === "string" &&
    isWindowSummary(data.current) &&
    isWindowSummary(data.prior) &&
    !!data.deltas &&
    typeof data.deltas === "object" &&
    isNullableNumber(data.deltas.billedAmount) &&
    Array.isArray(data.trend) &&
    data.trend.every(isTrendPoint) &&
    Array.isArray(data.topClinics) &&
    data.topClinics.every(isClinicRow) &&
    Array.isArray(data.hcpcsMix) &&
    data.hcpcsMix.every(isHcpcsRow) &&
    Array.isArray(data.queueSummary) &&
    data.queueSummary.every(isQueueRow)
  );
}
